import React, { useCallback, useEffect, useState, useRef } from 'react';
// Reference board dimensions (the size at which positions were authored)
const REF_WIDTH = 950;
const REF_HEIGHT = 700;
interface DraggableNote {
  id: string;
  xPct: number;
  yPct: number;
  rotation: number;
  src: string;
  widthPct: number;
  link?: string;
}
export function MoodBoard() {
  const [notes, setNotes] = useState<DraggableNote[]>([{
    id: 'note-1',
    xPct: 80 / REF_WIDTH * 100,
    yPct: 60 / REF_HEIGHT * 100,
    rotation: -2,
    src: 'https://cdn.magicpatterns.com/uploads/54kDY1kMSKDb4v8bzcN9KA/one_thing_i_wish_i_had_access_to_but_dont_is....png',
    widthPct: 280 / REF_WIDTH * 100
  }, {
    id: 'note-2',
    xPct: 420 / REF_WIDTH * 100,
    yPct: 120 / REF_HEIGHT * 100,
    rotation: 3,
    src: 'https://cdn.magicpatterns.com/uploads/c95RsM2CoXNgXW4kqrVC84/Untitled_(Instagram_Post)_(1).png',
    widthPct: 240 / REF_WIDTH * 100
  }, {
    id: 'note-4',
    xPct: 580 / REF_WIDTH * 100,
    yPct: 380 / REF_HEIGHT * 100,
    rotation: 2,
    src: 'https://cdn.magicpatterns.com/uploads/hb9AZmZEbhQtveoBE9LtBj/10.png',
    widthPct: 250 / REF_WIDTH * 100
  }, {
    id: 'note-5',
    xPct: 700 / REF_WIDTH * 100,
    yPct: 50 / REF_HEIGHT * 100,
    rotation: -1,
    src: 'https://cdn.magicpatterns.com/uploads/ojrmgm4DZ2oBpSrbxVm4vG/11.png',
    widthPct: 160 / REF_WIDTH * 100,
    link: 'https://www.plumia.org/'
  }, {
    id: 'note-6',
    xPct: 60 / REF_WIDTH * 100,
    yPct: 380 / REF_HEIGHT * 100,
    rotation: 3,
    src: 'https://cdn.magicpatterns.com/uploads/5uGYfTBEq9N1kGRngMcftU/one_thing_i_wish_i_had_access_to_but_dont_is..._(9).png',
    widthPct: 220 / REF_WIDTH * 100
  }, {
    id: 'note-7',
    xPct: 320 / REF_WIDTH * 100,
    yPct: 420 / REF_HEIGHT * 100,
    rotation: -3,
    src: 'https://cdn.magicpatterns.com/uploads/fzJUVKnr6vDRPQgA1TvRp3/one_thing_i_wish_i_had_access_to_but_dont_is..._(10).png',
    widthPct: 240 / REF_WIDTH * 100
  }, {
    id: 'note-8',
    xPct: 650 / REF_WIDTH * 100,
    yPct: 280 / REF_HEIGHT * 100,
    rotation: 1,
    src: 'https://cdn.magicpatterns.com/uploads/uae3MU9cdEBM8p3uE49z7Z/16.png',
    widthPct: 220 / REF_WIDTH * 100
  }, {
    id: 'note-9',
    xPct: 350 / REF_WIDTH * 100,
    yPct: 260 / REF_HEIGHT * 100,
    rotation: -2,
    src: 'https://cdn.magicpatterns.com/uploads/5ypq78aCwLRaXHxDPh9d82/_.png',
    widthPct: 230 / REF_WIDTH * 100,
    link: 'https://partiful.com/e/bgg231bgLaWYImRp1ZCw'
  }, {
    id: 'tape-1',
    xPct: 195 / REF_WIDTH * 100,
    yPct: 50 / REF_HEIGHT * 100,
    rotation: -1,
    src: 'https://cdn.magicpatterns.com/uploads/jnCZzXWTzSrokoyQeTJB7a/Screenshot_2026-02-15_at_11.52.31_PM.png',
    widthPct: 50 / REF_WIDTH * 100
  }, {
    id: 'tape-2',
    xPct: 515 / REF_WIDTH * 100,
    yPct: 110 / REF_HEIGHT * 100,
    rotation: 2,
    src: 'https://cdn.magicpatterns.com/uploads/vSWT1hGyaqhS3M5ZfnebZT/Screenshot_2026-02-15_at_11.52.59_PM.png',
    widthPct: 50 / REF_WIDTH * 100
  }]);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [boardSize, setBoardSize] = useState({
    width: REF_WIDTH,
    height: REF_HEIGHT
  });
  // Canvas pan offset in pixels
  const [panOffset, setPanOffset] = useState({
    x: 0,
    y: 0
  });
  const dragRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    origXPct: number;
    origYPct: number;
    didDrag: boolean;
  } | null>(null);
  // Pan drag ref (dragging empty space)
  const panDragRef = useRef<{
    startX: number;
    startY: number;
    origPanX: number;
    origPanY: number;
  } | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  // Measure board on mount and resize
  useEffect(() => {
    const measure = () => {
      if (boardRef.current) {
        setBoardSize({
          width: boardRef.current.offsetWidth,
          height: boardRef.current.offsetHeight
        });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  // --- Note dragging ---
  const handleNotePointerDown = useCallback((e: React.PointerEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation(); // Don't trigger pan
    const note = notes.find(n => n.id === id);
    if (!note) return;
    dragRef.current = {
      id,
      startX: e.clientX,
      startY: e.clientY,
      origXPct: note.xPct,
      origYPct: note.yPct,
      didDrag: false
    };
    // Bring to front
    setNotes(prev => {
      const dragged = prev.find(n => n.id === id)!;
      const rest = prev.filter(n => n.id !== id);
      return [...rest, dragged];
    });
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
  }, [notes]);
  const handleNotePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const {
      id,
      startX,
      startY,
      origXPct,
      origYPct
    } = dragRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragRef.current.didDrag = true;
    }
    const dxPct = dx / boardSize.width * 100;
    const dyPct = dy / boardSize.height * 100;
    setNotes(prev => prev.map(n => n.id === id ? {
      ...n,
      xPct: origXPct + dxPct,
      yPct: origYPct + dyPct
    } : n));
  }, [boardSize]);
  const handleNotePointerUp = useCallback(() => {
    if (dragRef.current && !dragRef.current.didDrag) {
      const note = notes.find(n => n.id === dragRef.current!.id);
      if (note?.link) {
        window.open(note.link, '_blank', 'noopener,noreferrer');
      }
    }
    dragRef.current = null;
  }, [notes]);
  // --- Canvas panning (drag on empty space) ---
  const handleBoardPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    panDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origPanX: panOffset.x,
      origPanY: panOffset.y
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [panOffset]);
  const handleBoardPointerMove = useCallback((e: React.PointerEvent) => {
    if (!panDragRef.current) return;
    const {
      startX,
      startY,
      origPanX,
      origPanY
    } = panDragRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    setPanOffset({
      x: origPanX + dx,
      y: origPanY + dy
    });
  }, []);
  const handleBoardPointerUp = useCallback(() => {
    panDragRef.current = null;
  }, []);
  return <div className="w-full pt-6 flex justify-center px-4 sm:px-6 md:px-10 pb-10" data-id="element-44">
      <div ref={boardRef} className="w-full max-w-[950px] bg-[#D5D9DD] relative overflow-hidden cursor-grab active:cursor-grabbing h-[60vh] md:h-[85vh]" style={{
      touchAction: 'none'
    }} onPointerDown={handleBoardPointerDown} onPointerMove={handleBoardPointerMove} onPointerUp={handleBoardPointerUp} data-id="element-45">
        {/* Inner canvas layer that pans */}
        <div style={{
        position: 'absolute',
        inset: 0,
        transform: `translate(${panOffset.x}px, ${panOffset.y}px)`
      }} data-id="element-46">
          {notes.map((note, index) => {
          const isHovered = hoveredId === note.id && !dragRef.current;
          const baseRotation = note.rotation;
          const hoverRotation = baseRotation + (baseRotation >= 0 ? 3 : -3);
          const left = note.xPct / 100 * boardSize.width;
          const top = note.yPct / 100 * boardSize.height;
          const width = note.widthPct / 100 * boardSize.width;
          return <div key={note.id} className="absolute cursor-grab active:cursor-grabbing select-none" style={{
            left,
            top,
            transform: isHovered ? `rotate(${hoverRotation}deg) scale(1.06) translateY(-6px)` : `rotate(${baseRotation}deg) scale(1) translateY(0px)`,
            zIndex: isHovered ? 999 : index + 1,
            filter: isHovered ? 'drop-shadow(4px 8px 16px rgba(0,0,0,0.22))' : 'drop-shadow(2px 4px 8px rgba(0,0,0,0.15))',
            touchAction: 'none',
            transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.25s ease, z-index 0s'
          }} onPointerEnter={() => setHoveredId(note.id)} onPointerLeave={() => setHoveredId(null)} onPointerDown={e => handleNotePointerDown(e, note.id)} onPointerMove={handleNotePointerMove} onPointerUp={handleNotePointerUp} data-id="element-47">
                <img src={note.src} alt="Sticky note" className="pointer-events-none" style={{
              width
            }} draggable={false} data-id="element-48" />
              </div>;
        })}
        </div>
      </div>
    </div>;
}